import { Styles } from 'react-select';

import { rgba } from 'polished';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export const InputBox = styled.input({
  border: 'none',
  backgroundColor: 'transparent',
  font: theme.font.regular16,
  color: theme.form.value.color,
  outline: 'none',
});

export const StyledMultiValue = styled.div<{ $color: string }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  border: `0.0625rem solid ${theme.form.value.multi.border.color}`,
  margin: '0.25rem 0.375rem 0.25rem 0',
  borderRadius: pxToRem(4),
  padding: '0.25rem 0.25rem 0.25rem 0.5rem',
  backgroundColor: theme.form.value.multi.bg.color,
  color: theme.form.value.multi.color,
  font: theme.font.semibold12,
  svg: {
    color: theme.form.value.multi.color,
  },
  ...(p.$color && {
    borderColor: p.$color,
    color: p.$color,
    backgroundColor: rgba(p.$color, 0.1),
    svg: {
      color: p.$color,
    },
  }),
  ':hover': {
    backgroundColor: theme.form.value.multi.bg.colorHover,
    color: theme.form.value.multi.colorHover,
    svg: {
      color: theme.form.value.multi.colorHover,
    },
    ...(p.$color && {
      backgroundColor: 'auto',
      color: 'auto',
      svg: {
        color: 'auto',
      },
    }),
    cursor: 'pointer',
  },
  span: {
    display: 'block',
    marginRight: '0.25rem',
  },
}));

export const styles: Styles<any, boolean> = {
  menu: (provided) => ({
    ...provided,
    backgroundColor: theme.color.backgroundLightDark,
    borderRadius: pxToRem(8),
    margin: 0,
    boxShadow: '0px 0.5rem 1.5rem rgba(0, 0, 0, 0.16)',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '1rem',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: theme.form.option.bg.color,
    font: theme.font.regular14,
    color: theme.form.option.color,
    minHeight: theme.base.height,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '0.25rem',
    padding: '0rem',
    ...(state.isFocused && {
      backgroundColor: theme.form.option.bg.colorFocus,
    }),
    ...(state.isDisabled && {
      opacity: '50%',
    }),
    ':hover': {
      cursor: 'pointer',
    },
    ':active': {},
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: theme.form.value.color,
  }),
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    font: theme.font.regular16,
    transition: transitionStandard('border-color', 'background-color'),
    borderWidth: theme.form.border.width,
    borderStyle: 'solid',
    borderColor: state.selectProps.error
      ? theme.color.error
      : state.isFocused
      ? theme.form.border.colorFocus
      : theme.form.border.color,
    backgroundColor: theme.form.bg.color,
    borderRadius: theme.form.border.radius,
    minHeight: theme.base.height,
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    outline: 'none',
    '&:hover': {},
    '&:active': {},
    '&:focus': {},
    ...(!state.isFocused && {
      ':hover': {
        backgroundColor: theme.form.bg.colorHover,
      },
    }),
    ...(state.isFocused && {
      backgroundColor: theme.form.bg.colorFocus,
    }),
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.isFocused ? theme.form.placeholder.colorFocus : theme.form.placeholder.color,
    marginLeft: pxToRem(4),
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    svg: {
      color: `${theme.form.icon.color} !important`,
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    font: theme.font.regular14,
    color: theme.color.textHelper,
  }),
};

export const FocusIconContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingRight: '0.75rem',
  svg: {
    color: theme.form.icon.color,
  },
});

export const OptionContainer = styled.div<{ $helpText: string }>((p) => ({
  padding: p.$helpText ? '0.75rem' : '0.625rem 0.75rem',
  font: p.$helpText ? theme.font.semibold14 : theme.font.regular14,
  display: 'flex',
  flexDirection: 'column',
}));

export const HelpText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textHelper,
});
